import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle, Phone, Building2, Zap, Shield, Clock } from 'lucide-react';

const CommercialElectrical = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Commercial Electrical Services
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Professional commercial electrical contractors serving businesses in London, Kingston, and surrounding areas. We provide comprehensive electrical solutions to keep your business running safely and efficiently.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
                <Link to="/contact">Request Quote</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
                <a href="tel:519-709-9242">
                  <Phone className="mr-2 h-5 w-5" />
                  Call 519-709-9242
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Electrical Solutions for Your Business
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Studerus Electric Inc. specializes in commercial electrical services for businesses of all sizes. From offices and retail spaces to industrial facilities and multi-unit residential buildings, we deliver reliable, code-compliant electrical solutions.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our team of 17 electricians and dedicated office staff ensures we can handle projects of any scale while minimizing disruption to your business operations.
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <p className="text-gray-700">Licensed Commercial Electricians</p>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <p className="text-gray-700">Flexible Scheduling to Minimize Downtime</p>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <p className="text-gray-700">Code-Compliant & Safety-Focused</p>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <p className="text-gray-700">Project Management & Coordination</p>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_next-day-electric/artifacts/hb97d018_20220624_114932.jpg"
                alt="Commercial electrical installation"
                className="rounded-lg shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Our Commercial Electrical Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <Building2 className="h-12 w-12 text-blue-900 mb-4" />
                <CardTitle>New Construction</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Complete electrical design</li>
                  <li>• Power distribution systems</li>
                  <li>• Lighting design & installation</li>
                  <li>• Emergency lighting systems</li>
                  <li>• ESA coordination</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Zap className="h-12 w-12 text-yellow-600 mb-4" />
                <CardTitle>Tenant Improvements</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Office fit-outs</li>
                  <li>• Retail space electrical</li>
                  <li>• Restaurant electrical</li>
                  <li>• Layout modifications</li>
                  <li>• Fast-track projects</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Shield className="h-12 w-12 text-green-600 mb-4" />
                <CardTitle>Maintenance & Repairs</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Preventive maintenance</li>
                  <li>• Emergency repairs</li>
                  <li>• Troubleshooting</li>
                  <li>• System upgrades</li>
                  <li>• 24/7 emergency service</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Building2 className="h-12 w-12 text-purple-600 mb-4" />
                <CardTitle>Lighting Solutions</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• LED retrofit projects</li>
                  <li>• Parking lot lighting</li>
                  <li>• Interior lighting design</li>
                  <li>• Exterior building lighting</li>
                  <li>• Energy-efficient solutions</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Zap className="h-12 w-12 text-red-600 mb-4" />
                <CardTitle>Power Systems</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Panel upgrades & replacements</li>
                  <li>• Three-phase power</li>
                  <li>• Generator installations</li>
                  <li>• UPS systems</li>
                  <li>• Load calculations</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Shield className="h-12 w-12 text-blue-600 mb-4" />
                <CardTitle>Safety & Compliance</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Code compliance upgrades</li>
                  <li>• Fire alarm systems</li>
                  <li>• Exit & emergency lighting</li>
                  <li>• Safety inspections</li>
                  <li>• ESA certification support</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Building2 className="h-12 w-12 text-indigo-600 mb-4" />
                <CardTitle>Data & Communications</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• Structured cabling</li>
                  <li>• Network infrastructure</li>
                  <li>• Server room electrical</li>
                  <li>• Telecom installations</li>
                  <li>• Security system wiring</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Zap className="h-12 w-12 text-orange-600 mb-4" />
                <CardTitle>Specialized Systems</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• EV charging stations</li>
                  <li>• Solar panel integration</li>
                  <li>• HVAC electrical</li>
                  <li>• Industrial equipment</li>
                  <li>• Access control systems</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Clock className="h-12 w-12 text-teal-600 mb-4" />
                <CardTitle>Emergency Services</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-gray-600">
                  <li>• 24/7 emergency response</li>
                  <li>• Power outage troubleshooting</li>
                  <li>• After-hours service</li>
                  <li>• Critical system repairs</li>
                  <li>• Weekend availability</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Industries We Serve
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="font-semibold text-gray-900">Offices</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="font-semibold text-gray-900">Retail Stores</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="font-semibold text-gray-900">Restaurants</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="font-semibold text-gray-900">Warehouses</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="font-semibold text-gray-900">Medical Facilities</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="font-semibold text-gray-900">Schools</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="font-semibold text-gray-900">Hotels</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="font-semibold text-gray-900">Manufacturing</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-blue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12">
            Why Businesses Choose Studerus Electric
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-xl text-gray-900 mb-3">Large Team Capacity</h3>
              <p className="text-gray-600">With 17 electricians on staff, we can handle projects of any size and meet tight deadlines.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-xl text-gray-900 mb-3">Minimal Disruption</h3>
              <p className="text-gray-600">We work around your schedule, including evenings and weekends, to keep your business running.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-xl text-gray-900 mb-3">Project Management</h3>
              <p className="text-gray-600">Dedicated office staff coordinates every detail for smooth project execution.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-xl text-gray-900 mb-3">Safety Focused</h3>
              <p className="text-gray-600">All work performed to code with comprehensive safety protocols and documentation.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-xl text-gray-900 mb-3">Transparent Pricing</h3>
              <p className="text-gray-600">Detailed quotes and clear communication throughout your project.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-xl text-gray-900 mb-3">Local Experience</h3>
              <p className="text-gray-600">Serving London, Kingston, and surrounding Ontario communities for years.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Start Your Commercial Project?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contact Studerus Electric Inc. today for professional commercial electrical services
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold">
              <Link to="/contact">Request Quote</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900">
              <a href="tel:519-709-9242">
                <Phone className="mr-2 h-5 w-5" />
                Call 519-709-9242
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CommercialElectrical;
